import { GUIDES } from "@/data/civilGuides";
// Civil Hub how-to guides — /guides/:slug. Config-driven, dark theme, calculator-linked.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, BookOpen, Link2, Check, Facebook, Linkedin, Mail, MessageCircle, Share2 } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "../FAQ";
import { MarketingHeader, Breadcrumb, breadcrumbJsonLd } from "./SeoProductPage";
import { hubTrack } from "./Calculators";

export { GUIDES };

const articleJsonLd = (g) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: g.name,
  description: g.description,
  author: { "@type": "Organization", name: "Fieldlink" },
  publisher: { "@type": "Organization", name: "Fieldlink", url: "https://www.fieldlink.co.nz" },
  mainEntityOfPage: `https://www.fieldlink.co.nz/guides/${g.slug}`,
});

const XLogo = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

function ShareBar({ guide }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : `https://www.fieldlink.co.nz/guides/${guide.slug}`;
  const text = `${guide.name} — free guide on the Fieldlink Civil Hub`;
  const track = (network) => hubTrack("guide_shared", { tool: `guide:${guide.slug}:${network}` });
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); track("copy"); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  const open = (network, href) => { track(network); window.open(href, "_blank", "noopener,noreferrer,width=600,height=520"); };
  const enc = encodeURIComponent;
  const btnCls = "w-9 h-9 rounded-md border border-[#cbd3d5] text-[#52616c] hover:border-[#FF5F15] hover:text-[#a63c0b] inline-flex items-center justify-center transition-colors";
  return (
    <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="guide-share-bar">
      <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#52616c] inline-flex items-center gap-1.5 mr-1"><Share2 className="w-3.5 h-3.5" /> Share</span>
      <button onClick={copyLink} className={`${btnCls} w-auto px-3 gap-1.5 text-[11px] font-bold uppercase tracking-wider`} data-testid="guide-share-copy" aria-label="Copy link">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Link2 className="w-3.5 h-3.5" />}{copied ? "Copied" : "Copy link"}
      </button>
      <button onClick={() => open("facebook", `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`)} className={btnCls} data-testid="guide-share-facebook" aria-label="Share on Facebook"><Facebook className="w-4 h-4" /></button>
      <button onClick={() => open("linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`)} className={btnCls} data-testid="guide-share-linkedin" aria-label="Share on LinkedIn"><Linkedin className="w-4 h-4" /></button>
      <button onClick={() => open("x", `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`)} className={btnCls} data-testid="guide-share-x" aria-label="Share on X"><XLogo className="w-3.5 h-3.5" /></button>
      <button onClick={() => open("whatsapp", `https://wa.me/?text=${enc(`${text} ${url}`)}`)} className={btnCls} data-testid="guide-share-whatsapp" aria-label="Share on WhatsApp"><MessageCircle className="w-4 h-4" /></button>
      <a href={`mailto:?subject=${enc(guide.name)}&body=${enc(`${text}\n${url}`)}`} onClick={() => track("email")} className={btnCls} data-testid="guide-share-email" aria-label="Share by email"><Mail className="w-4 h-4" /></a>
    </div>
  );
}

const Block = ({ title, lines, mono = true }) => (
  <div className="rounded-lg border border-[#cbd3d5] bg-white p-4 my-4">
    <p className="text-[10px] font-bold uppercase tracking-widest text-[#a63c0b] mb-2">{title}</p>
    {lines.map((l) => <p key={l} className={`text-[12.5px] ${mono ? "font-mono" : ""} text-[#465562] leading-relaxed`}>{l}</p>)}
  </div>
);

export function GuidePage({ slug }) {
  const g = GUIDES.find((x) => x.slug === slug);
  useEffect(() => { hubTrack("template_started", { tool: `guide:${g.slug}` }); }, [g.slug]);
  const trail = [{ label: "Home", to: "/" }, { label: "Civil Hub", to: "/resources" }, { label: g.name }];
  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO title={g.title} description={g.description} path={`/guides/${g.slug}`} jsonLd={[breadcrumbJsonLd(trail), articleJsonLd(g)]} />
      <MarketingHeader />
      <Breadcrumb trail={trail} />
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-10">
        <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-3">Civil Hub guide</div>
        <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl leading-tight" data-testid="guide-h1">{g.name}</h1>
        <ShareBar guide={g} />

        {/* quick answer — answer the question immediately */}
        <div className="mt-6 rounded-lg border border-[#FF5F15]/40 bg-[#FF5F15]/[0.06] p-5" data-testid="guide-quick-answer">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#a63c0b] mb-1.5">Quick answer</p>
          <p className="text-[14.5px] text-[#344451] leading-relaxed">{g.quickAnswer}</p>
        </div>

        {/* calculator CTA near the top */}
        <Link to={g.calculator.to} onClick={() => hubTrack("related_tool_clicked", { tool: g.slug })}
          className="mt-4 flex items-center justify-between rounded-lg border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 p-4 transition-colors group" data-testid="guide-calculator-link">
          <span className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-[#FF5F15]/12 border border-[#FF5F15]/40 flex items-center justify-center"><Calculator className="w-4 h-4 text-[#a63c0b]" /></span>
            <span>
              <span className="block text-[10px] font-mono font-bold uppercase tracking-widest text-[#52616c]">Skip the maths — free tool</span>
              <span className="block font-display font-bold uppercase tracking-tight text-[15px]">{g.calculator.label}</span>
            </span>
          </span>
          <ArrowRight className="w-4 h-4 text-[#52616c] group-hover:text-[#a63c0b]" />
        </Link>

        <nav aria-label="On this page" className="mt-7 border-y border-[#cbd3d5] py-4"><p className="font-semibold text-sm mb-2">On this page</p><ul className="space-y-2">{g.sections.map((s, i) => <li key={s.h}><a href={`#section-${i + 1}`} className="text-sm text-[#465562] underline underline-offset-4 hover:text-[#a63c0b]">{s.h}</a></li>)}</ul></nav>
        {g.sections.map((s, i) => (
          <section key={s.h} id={`section-${i + 1}`} className="mt-9 scroll-mt-24">
            <h2 className="font-display font-black uppercase tracking-tight text-xl">{s.h}</h2>
            {(s.body || []).map((p) => <p key={p.slice(0, 40)} className="text-[14px] text-[#465562] leading-relaxed mt-3">{p}</p>)}
            {s.formula && <Block title="Formulas" lines={s.formula} />}
            {s.example && <Block title="Worked example" lines={s.example} />}
            {s.list && (
              <ul className="mt-3 space-y-2">
                {s.list.map((li) => (
                  <li key={li.slice(0, 40)} className="flex gap-2.5 text-[14px] text-[#465562] leading-relaxed"><span className="text-[#a63c0b] font-bold shrink-0">—</span>{li}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {/* product CTA */}
        <div className="mt-11 rounded-lg border border-[#cbd3d5] bg-[#e9eeec] p-6 flex flex-wrap items-center justify-between gap-4" data-testid="guide-product-cta">
          <p className="text-sm text-[#465562] max-w-md">{g.product.text}</p>
          <Link to={g.product.to} onClick={() => hubTrack("fieldlink_cta_clicked", { tool: `guide:${g.slug}` })}
            className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-xs font-bold uppercase tracking-widest px-5 py-3 inline-flex items-center gap-1.5">{g.product.label} <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>

        {/* related */}
        <div className="mt-8">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#52616c] mb-2">Related</p>
          <div className="flex flex-wrap gap-2">
            {g.related.map((r) => {
              const guide = GUIDES.find((x) => `/guides/${x.slug}` === r);
              const label = guide ? guide.name : r.split("/").pop().replace(/-/g, " ");
              return <Link key={r} to={r} className="text-xs font-bold uppercase tracking-wider rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 text-[#344451] px-3 py-1.5" data-testid={`guide-related-${r.split("/").pop()}`}>{label}</Link>;
            })}
            <Link to="/resources" className="text-xs font-bold uppercase tracking-wider rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 text-[#344451] px-3 py-1.5">All Civil Hub tools</Link>
          </div>
        </div>
      </article>
      <PublicFooter />
    </div>
  );
}

export const GuideIcon = BookOpen;
